import React, { useEffect, useRef, useCallback } from 'react';
import { trackVideoPlay, trackVideoComplete, trackVideoProgress, getSessionId } from '../services/api';
import { saveWatchProgress, getWebinarProgress, markAsComplete } from '../utils/watchProgress';

// Session storage key for tracking fired milestones
const getMilestoneKey = (webinarId) => `video_milestones_${webinarId}`;

// Get fired milestones for current session
const getFiredMilestones = (webinarId) => {
  const sessionId = getSessionId();
  const key = getMilestoneKey(webinarId);
  const stored = sessionStorage.getItem(key);
  if (stored) {
    const data = JSON.parse(stored);
    // Only use if same session
    if (data.sessionId === sessionId) {
      return data.milestones;
    }
  }
  return { play: false, 25: false, 50: false, 75: false, complete: false };
};

// Save fired milestone for current session
const saveFiredMilestone = (webinarId, milestone) => {
  const sessionId = getSessionId();
  const key = getMilestoneKey(webinarId);
  const current = getFiredMilestones(webinarId);
  current[milestone] = true;
  sessionStorage.setItem(key, JSON.stringify({
    sessionId,
    milestones: current
  }));
};

// Debounce delay in ms - prevents rapid-fire events during scrubbing
const DEBOUNCE_DELAY = 1000;

// Track the highest percentage naturally reached (not by scrubbing)
const getHighestNaturalProgress = (webinarId) => {
  const key = `video_natural_progress_${webinarId}`;
  return parseInt(sessionStorage.getItem(key) || '0', 10);
};

const setHighestNaturalProgress = (webinarId, percentage) => {
  const key = `video_natural_progress_${webinarId}`;
  const current = getHighestNaturalProgress(webinarId);
  if (percentage > current) {
    sessionStorage.setItem(key, percentage.toString());
  }
};

const WistiaPlayer = ({ mediaId, webinarId, webinarTitle, onReady }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const progressTracked = useRef(null);
  const lastTimeRef = useRef(0);
  const debounceTimers = useRef({});
  const isSeekingRef = useRef(false);

  const onReadyRef = useRef(onReady);
  const webinarTitleRef = useRef(webinarTitle);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
  useEffect(() => { webinarTitleRef.current = webinarTitle; }, [webinarTitle]);

  // Debounced milestone tracking to prevent rapid-fire during scrubbing
  const trackMilestoneDebounced = useCallback((milestone, videoTime, trackFn) => {
    // Clear any existing timer for this milestone
    if (debounceTimers.current[milestone]) {
      clearTimeout(debounceTimers.current[milestone]);
    }
    
    // Set a new debounced timer
    debounceTimers.current[milestone] = setTimeout(() => {
      // Double-check milestone hasn't been fired yet
      if (!progressTracked.current[milestone]) {
        console.log(`Video ${milestone}% progress tracked (debounced)`);
        trackFn();
        progressTracked.current[milestone] = true;
        saveFiredMilestone(webinarId, milestone.toString());
      }
    }, DEBOUNCE_DELAY);
  }, [webinarId]);

  useEffect(() => {
    if (!mediaId || !containerRef.current) return;

    // Initialize progress tracking from session storage
    progressTracked.current = getFiredMilestones(webinarId);

    // Load Wistia script if not already loaded
    const loadWistiaScript = () => {
      return new Promise((resolve, reject) => {
        try {
          if (window.Wistia) {
            resolve();
            return;
          }

          const script = document.createElement('script');
          script.src = 'https://fast.wistia.com/assets/external/E-v1.js';
          script.async = true;
          script.onload = () => resolve();
          script.onerror = (err) => {
            console.error('Failed to load Wistia script:', err);
            reject(new Error('Failed to load Wistia script'));
          };
          document.head.appendChild(script);
        } catch (error) {
          console.error('Error in loadWistiaScript:', error);
          reject(error);
        }
      });
    };

    // Safe event binding helper
    const safeBindEvent = (video, eventName, handler) => {
      try {
        if (video && typeof video.bind === 'function') {
          video.bind(eventName, handler);
        }
      } catch (error) {
        console.error(`Error binding ${eventName} event:`, error);
      }
    };

    // Initialize Wistia player
    const initPlayer = async () => {
      try {
        await loadWistiaScript();

        // Define custom skip controls in Wistia's native control bar (once)
        if (window.Wistia && !window._skipControlsDefined) {
          window._skipControlsDefined = true;

          // Build SVG elements safely via DOM API instead of innerHTML
          const createSkipSvg = (pathD, textContent) => {
            const NS = 'http://www.w3.org/2000/svg';
            const svg = document.createElementNS(NS, 'svg');
            svg.setAttribute('style', 'display:block');
            svg.setAttribute('width', '20');
            svg.setAttribute('height', '20');
            svg.setAttribute('viewBox', '0 0 24 24');
            svg.setAttribute('fill', 'none');
            svg.setAttribute('stroke', 'white');
            svg.setAttribute('stroke-width', '2');
            svg.setAttribute('stroke-linecap', 'round');
            svg.setAttribute('stroke-linejoin', 'round');
            const path = document.createElementNS(NS, 'path');
            path.setAttribute('d', pathD);
            svg.appendChild(path);
            const text = document.createElementNS(NS, 'text');
            text.setAttribute('x', '12');
            text.setAttribute('y', '15.5');
            text.setAttribute('text-anchor', 'middle');
            text.setAttribute('font-size', '7');
            text.setAttribute('fill', 'white');
            text.setAttribute('stroke', 'none');
            text.setAttribute('font-weight', 'bold');
            text.textContent = textContent;
            svg.appendChild(text);
            return svg;
          };

          const styleButton = (root, title, testId) => {
            root.style.cursor = 'pointer';
            root.style.display = 'flex';
            root.style.alignItems = 'center';
            root.style.justifyContent = 'center';
            root.setAttribute('title', title);
            root.setAttribute('data-testid', testId);
          };

          class SkipBackControl {
            constructor(video) { this.video = video; }
            mountButton(buttonRoot) {
              buttonRoot.appendChild(createSkipSvg('M1 4v6h6', '10'));
              // Second path for the arc
              const NS = 'http://www.w3.org/2000/svg';
              const arcPath = document.createElementNS(NS, 'path');
              arcPath.setAttribute('d', 'M3.51 15a9 9 0 1 0 2.13-9.36L1 10');
              buttonRoot.firstChild.appendChild(arcPath);
              styleButton(buttonRoot, 'Skip back 10s', 'skip-backward-btn');
              buttonRoot.addEventListener('click', () => {
                const t = this.video.time();
                this.video.time(Math.max(0, t - 10));
              });
            }
          }
          SkipBackControl.handle = 'skipBackward';
          SkipBackControl.type = 'control-bar-right';
          SkipBackControl.sortValue = 0;

          class SkipFwdControl {
            constructor(video) { this.video = video; }
            mountButton(buttonRoot) {
              buttonRoot.appendChild(createSkipSvg('M23 4v6h-6', '10'));
              const NS = 'http://www.w3.org/2000/svg';
              const arcPath = document.createElementNS(NS, 'path');
              arcPath.setAttribute('d', 'M20.49 15a9 9 0 1 1-2.13-9.36L23 10');
              buttonRoot.firstChild.appendChild(arcPath);
              styleButton(buttonRoot, 'Skip forward 10s', 'skip-forward-btn');
              buttonRoot.addEventListener('click', () => {
                const t = this.video.time();
                const d = this.video.duration();
                this.video.time(Math.min(d, t + 10));
              });
            }
          }
          SkipFwdControl.handle = 'skipForward';
          SkipFwdControl.type = 'control-bar-right';
          SkipFwdControl.sortValue = 1;

          window.Wistia.defineControl(SkipBackControl);
          window.Wistia.defineControl(SkipFwdControl);
        }

        // Create Wistia embed
        const embedScript = document.createElement('script');
        embedScript.src = `https://fast.wistia.com/embed/medias/${mediaId}.jsonp`;
        embedScript.async = true;
        embedScript.onerror = (err) => {
          console.error('Failed to load Wistia media embed:', err);
        };
        document.head.appendChild(embedScript);

        // Wait for Wistia to be ready
        const checkWistia = setInterval(() => {
          try {
            if (window.Wistia && window._wq) {
              clearInterval(checkWistia);
              
              window._wq = window._wq || [];
              window._wq.push({
                id: mediaId,
                onReady: (video) => {
                  try {
                    if (!video) {
                      console.error('Wistia video object is null');
                      return;
                    }
                    
                    videoRef.current = video;
                    
                    // Resume from saved position if available
                    try {
                      const savedProgress = getWebinarProgress(webinarId);
                      if (savedProgress && savedProgress.currentTime && savedProgress.percentage < 95) {
                        video.time(savedProgress.currentTime);
                      }
                    } catch (err) {
                      console.error('Error resuming video position:', err);
                    }

                    // Detect seeking/scrubbing
                    safeBindEvent(video, 'seek', () => {
                      isSeekingRef.current = true;
                      // Reset seeking flag after a short delay
                      setTimeout(() => {
                        isSeekingRef.current = false;
                      }, 500);
                    });

                    // Track video play event (only once per session)
                    safeBindEvent(video, 'play', () => {
                      try {
                        if (progressTracked.current && !progressTracked.current.play) {
                          console.log('Video play event tracked');
                          const currentTime = video.time ? video.time() : 0;
                          trackVideoPlay(webinarId, currentTime).catch(err => 
                            console.error('Error in trackVideoPlay:', err)
                          );
                          progressTracked.current.play = true;
                          saveFiredMilestone(webinarId, 'play');
                        }
                      } catch (err) {
                        console.error('Error in play event handler:', err);
                      }
                    });

                    // Track video completion (only once per session)
                    safeBindEvent(video, 'end', () => {
                      try {
                        if (progressTracked.current && !progressTracked.current.complete) {
                          console.log('Video complete event tracked');
                          const duration = video.duration ? video.duration() : 0;
                          trackVideoComplete(webinarId, duration).catch(err => 
                            console.error('Error in trackVideoComplete:', err)
                          );
                          progressTracked.current.complete = true;
                          saveFiredMilestone(webinarId, 'complete');
                          markAsComplete(webinarId);
                        }
                      } catch (err) {
                        console.error('Error in end event handler:', err);
                      }
                    });

                    // Track progress milestones and save progress
                    safeBindEvent(video, 'secondchange', (s) => {
                      try {
                        if (!video.duration) return;
                        
                        const duration = video.duration();
                        if (!duration || duration <= 0) return;
                        
                        const percentage = Math.round((s / duration) * 100);
                        const timeDiff = Math.abs(s - lastTimeRef.current);
                        lastTimeRef.current = s;

                        // Detect if user is scrubbing (time jump > 3 seconds)
                        const isScrubbing = timeDiff > 3 || isSeekingRef.current;

                        // Save watch progress every 5 seconds (for Continue Watching feature)
                        if (Math.floor(s) % 5 === 0) {
                          try {
                            saveWatchProgress(webinarId, {
                              currentTime: s,
                              duration: duration,
                              percentage: percentage,
                              title: webinarTitleRef.current || 'Webinar',
                            });
                          } catch (err) {
                            console.error('Error saving watch progress:', err);
                          }
                        }

                        // Track natural progress (not from scrubbing)
                        if (!isScrubbing) {
                          setHighestNaturalProgress(webinarId, percentage);
                        }

                        // Get the highest naturally reached percentage
                        const highestNatural = getHighestNaturalProgress(webinarId);

                        // Track 25% milestone (only if naturally reached, with debounce)
                        if (percentage >= 25 && progressTracked.current && !progressTracked.current[25]) {
                          if (highestNatural >= 20 || !isScrubbing) {
                            trackMilestoneDebounced(25, s, () => 
                              trackVideoProgress(webinarId, 25, s).catch(err => 
                                console.error('Error tracking 25% progress:', err)
                              )
                            );
                          }
                        }

                        // Track 50% milestone
                        if (percentage >= 50 && progressTracked.current && !progressTracked.current[50]) {
                          if (highestNatural >= 45 || !isScrubbing) {
                            trackMilestoneDebounced(50, s, () => 
                              trackVideoProgress(webinarId, 50, s).catch(err => 
                                console.error('Error tracking 50% progress:', err)
                              )
                            );
                          }
                        }

                        // Track 75% milestone
                        if (percentage >= 75 && progressTracked.current && !progressTracked.current[75]) {
                          if (highestNatural >= 70 || !isScrubbing) {
                            trackMilestoneDebounced(75, s, () => 
                              trackVideoProgress(webinarId, 75, s).catch(err => 
                                console.error('Error tracking 75% progress:', err)
                              )
                            );
                          }
                        }
                      } catch (err) {
                        console.error('Error in secondchange handler:', err);
                      }
                    });

                    if (onReadyRef.current) {
                      try {
                        onReadyRef.current(video);
                      } catch (err) {
                        console.error('Error in onReady callback:', err);
                      }
                    }
                  } catch (err) {
                    console.error('Error in Wistia onReady handler:', err);
                  }
                },
                onEmbedFailed: (error) => {
                  console.error('Wistia embed failed:', error);
                }
              });
            }
          } catch (err) {
            console.error('Error checking Wistia readiness:', err);
            clearInterval(checkWistia);
          }
        }, 100);

        // Cleanup timeout after 10 seconds
        setTimeout(() => clearInterval(checkWistia), 10000);
      } catch (error) {
        console.error('Error initializing Wistia player:', error);
      }
    };

    initPlayer().catch(err => console.error('Error in initPlayer:', err));

    // Cleanup
    return () => {
      // Clear all debounce timers
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
      if (videoRef.current) {
        videoRef.current.remove();
      }
    };
  }, [mediaId, webinarId, trackMilestoneDebounced]);

  return (
    <div ref={containerRef} className="wistia-player-container">
      <div
        className={`wistia_embed wistia_async_${mediaId} videoFoam=true`}
        style={{ position: 'relative', width: '100%', height: '100%' }}
      >
        &nbsp;
      </div>
    </div>
  );
};

export default WistiaPlayer;
